import { useAuth } from '@/backend/context/auth-management';
import { ROUTES } from '@/lib/routes';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { useNavigate, useRouteError } from 'react-router';

export default function GlobalErrorBoundary({ error: errorProp }: { error?: unknown }) {
  const routeError = useRouteError();
  const error = errorProp || routeError;
  const navigate = useNavigate();
  const profile = useAuth((s) => s.profile);

  useEffect(() => {
    // Logueamos el error para que tú como dev sepas qué pasó
    console.error('[Firma:GlobalError] 🚨', error);

    // Identificar si es un error de carga de módulo (común después de un deploy)
    // Extraemos el mensaje de forma segura ya que el error puede no ser una instancia de Error
    const errorMessage =
      error instanceof Error
        ? error.message
        : typeof error === 'string'
          ? error
          : (error as any)?.message || '';

    const errorName = (error as any)?.name || '';

    const isChunkLoadError =
      errorMessage.includes('Failed to fetch dynamically imported module') ||
      errorMessage.includes('Importing a module script failed') ||
      errorName === 'ChunkLoadError';

    if (isChunkLoadError) {
      console.warn('[Firma:System] 🔄 Detectado error de módulo desactualizado. Forzando recarga...');
      // Recargamos la página para bajar la nueva versión del index.html
      const reloadTimer = setTimeout(() => {
        window.location.reload();
      }, 1000); // Dar un segundo para que el usuario vea el mensaje de "restableciendo"
      return () => clearTimeout(reloadTimer);
    }

    // Solo reportar a Sentry en producción
    if (import.meta.env.PROD) {
      // Identificar al usuario en Sentry si está disponible
      if (profile) {
        Sentry.setUser({
          id: profile.id,
          email: profile.email || undefined,
          slug: profile.public.branding.slug,
          username: profile.public.branding.name,
        });
      }

      // Reportar el error a Sentry con contexto enriquecido
      Sentry.withScope((scope) => {
        scope.setTag('app_context', 'global_error_boundary');
        if (profile) {
          scope.setContext('business', {
            id: profile.id,
            email: profile.email || undefined,
            slug: profile.public.branding.slug,
            username: profile.public.branding.name,
          });
        }
        Sentry.captureException(error);
      });
    }

    // Redirigir al índice después de 2 segundos para no crear bucles infinitos inmediatos
    // Solo si NO es un ChunkLoadError (porque eso ya lo manejamos arriba)
    const timer = setTimeout(() => {
      navigate(ROUTES.DASHBOARD.PRINCIPAL, { replace: true });
    }, 2000);

    return () => clearTimeout(timer);
  }, [error, navigate, profile]);

  return (
    <div className="bg-background flex h-screen w-full flex-col items-center justify-center p-6 text-center">
      <div className="relative mb-8 h-24 w-24">
        {/* Icono de error con aura dorada */}
        <div className="bg-ring/20 absolute inset-0 animate-pulse rounded-full blur-2xl" />
        <div className="border-ring/50 bg-card text-ring relative flex h-full w-full items-center justify-center rounded-full border-2 text-4xl font-black italic">
          !
        </div>
      </div>

      <h1 className="text-foreground mb-2 text-2xl font-black tracking-tighter uppercase italic">
        Algo no salió según el plan
      </h1>
      <p className="text-muted-foreground max-w-xs text-sm font-bold tracking-widest uppercase">
        Estamos restableciendo tu conexión con la élite...
      </p>

      {/* Barra de progreso sutil */}
      <div className="bg-secondary mt-8 h-1 w-48 overflow-hidden rounded-full">
        <div className="animate-progress bg-ring h-full shadow-[0_0_10px_rgba(212,175,55,0.5)]" />
      </div>
    </div>
  );
}
